import { api } from './client';

/**
 * Перенос ученика между курсами одного подтипа — кнопка этапа 8.
 *
 * Договор — `docs/CONTRACTS/enrollment-transfer.md` §9. Сервер — users-service, только admin; шлюз ведёт
 * `/admin/students/…` и `/admin/transfers/…` в users (`nginx.prod.conf`, строка `(users|enrollments|students|transfers)`).
 *
 * ⚠️ ПО УМОЛЧАНИЮ ПРОГОН ПРОБНЫЙ. Настоящий перенос — только `dry_run:false` вместе с `expect` из пробного: изменилось
 * что-то у ученика между прогоном и нажатием — сервер отвечает `409 rows_changed` с новым отчётом, ничего не записав.
 *
 * ⚠️ Пути — буквальные: сторож `adminRoutesInGateway.test.ts` читает их из кода и сверяет со шлюзом и службой.
 */

export type TransferCourse = { id: string; title: string | null; subtype: string | null };

/** Строки ученика по одной группе: в курсе-источнике, из них с парой в цели, уже в цели (до переноса — 0). */
export type TransferTableCount = { table: string; rows: number; paired: number; in_target: number };

/** Строка без пары по `mirror_key`: перенос с ней невозможен. */
export type TransferUnpairedRow = {
  table: string;
  row_key: string;
  node_id: string | null;
  kind: 'block' | 'section' | 'lesson' | 'unknown';
  title: string | null;
  block_type: string | null;
  has_mirror_key: boolean;
};

export type TransferVerifyGroup = { table: string; rows: number; ok: number };
export type TransferVerify = { ok: boolean; groups: TransferVerifyGroup[] };

export type TransferReport = {
  transfer_id: string | null;
  dry_run: boolean;
  student_id: string;
  from: TransferCourse;
  to: TransferCourse;
  src_enrollment_id: string | null;
  dst_enrollment_id: string | null;
  src_was_primary: boolean;
  tables: TransferTableCount[];
  unpaired: TransferUnpairedRow[];
  /** Коды нарушенных условий (§3 договора). Непустой список приходит в отказе, не в 200. */
  problems: string[];
  /** Отпечаток пробного прогона — обязателен для настоящего переноса. */
  expect?: string;
  verify?: TransferVerify;
  /** false — рейтинги обновятся фоном; перенос уже записан. */
  refresh_ok?: boolean;
};

export type TransferJournalRow = {
  id: string;
  student_id: string;
  from_marathon_id: string;
  from_title: string | null;
  to_marathon_id: string;
  to_title: string | null;
  transferred_by: string;
  counts: TransferTableCount[];
  verify: TransferVerify | null;
  refresh_ok: boolean | null;
  status: 'done' | 'rolled_back';
  created_at: string;
  rolled_back_at: string | null;
  rolled_back_by: string | null;
  /** Не отменён и после него переносов не было. */
  can_rollback: boolean;
};

export type TransferRollbackResult = {
  transfer_id: string;
  student_id: string;
  restored: { table: string; rows: number }[];
  rolled_back_at: string;
  refresh_ok: boolean;
};

export const enrollmentTransferApi = {
  /** Пробный прогон: только чтение, отчёт и `expect`. */
  async preview(studentId: string, from: string, to: string) {
    const { data } = await api.post(`/admin/students/${studentId}/transfer`, { from, to, dry_run: true });
    return data as TransferReport;
  },
  /** Настоящий перенос — с отпечатком пробного. */
  async run(studentId: string, from: string, to: string, expect: string) {
    const { data } = await api.post(`/admin/students/${studentId}/transfer`, { from, to, dry_run: false, expect });
    return data as TransferReport;
  },
  /** Журнал переносов ученика, новые сверху. */
  async list(studentId: string) {
    const { data } = await api.get(`/admin/students/${studentId}/transfers`);
    return (data ?? []) as TransferJournalRow[];
  },
  async rollback(transferId: string) {
    const { data } = await api.post(`/admin/transfers/${transferId}/rollback`);
    return data as TransferRollbackResult;
  },
};
